import { ColumnDef } from '@tanstack/react-table';
import {
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { FormatOptionEntries } from 'src/modals/EditTableColumns';
import { CustomColumnName } from 'src/modals/InventoryTableColumns/CustomColumnName';
import { CustomNumericCell } from 'src/tables/ListingTable/configs/CustomNumericCell';
import {
  SECTION_TYPE_TO_PRECISION_SETTING,
  SectionType,
} from 'src/utils/types/sectionType';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';

interface CustomColumnOptions<TData> {
  /** Column ID */
  columnId: string;
  /** Formula to evaluate */
  formula?: string | null;
  /** Section type for precision settings */
  sectionType?: SectionType;
  /** Accessor function to evaluate the formula and return the value */
  formulaAccessor: (
    data: TData,
    formula: string | null | undefined
  ) => number | null;
  /** Accessor function to get currency code from data */
  currencyAccessor: (data: TData) => string | undefined;
  /** Accessor function to get custom columns array */
  customColumnsAccessor: () => any[];
  /** Whether sorting is enabled for this column. Pass false in Search V2 contexts
   * where sorting is server-side and custom column IDs are not registered in the
   * backend sort mapper. */
  enableSorting?: boolean;
}

/**
 * Factory function to create custom column definitions for tables.
 * Evaluates formulas and displays numeric results with user-defined precision.
 *
 * @example
 * createCustomColumn({
 *   columnId: 'custom-margin',
 *   formula: '(sale.ttlNetProcs.amt - sale.soldTktCost.amt) / sale.ttlNetProcs.amt * 100',
 *   sectionType: SectionType.Sales,
 *   formulaAccessor: (data, formula) => formula && data ? evaluateFormula(formula, data.sale) : null,
 *   currencyAccessor: (data) => data?.sale?.currency,
 *   customColumnsAccessor: () => customColumns,
 * })
 */
export const createCustomColumn = <TData,>(
  options: CustomColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    columnId,
    formula,
    sectionType = SectionType.Sales,
    formulaAccessor,
    currencyAccessor,
    customColumnsAccessor,
    enableSorting,
  } = options;

  return {
    id: columnId,
    ...(enableSorting !== undefined && { enableSorting }),
    header: ({ table }) => (
      <TableHeaderCellDiv align="right">
        <CustomColumnName
          id={columnId}
          sectionType={table.options.meta?.sectionType ?? sectionType}
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => formulaAccessor(data, formula),
    cell: function Cell({ getValue, row: { original }, table }) {
      const { value: storedColumnNumberPrecisions = {} } =
        useServerUserSetting<FormatOptionEntries>({
          id: SECTION_TYPE_TO_PRECISION_SETTING[
            table.options.meta?.sectionType ?? sectionType
          ],
        });

      const customColumns = customColumnsAccessor();
      const userDefinedPrecision = storedColumnNumberPrecisions[columnId];

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={getValue<number | null>()}
          isLoading={Boolean(!original)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? sectionType}
          matchingCustomColumn={customColumns.find(
            (c: any) => c.id === columnId
          )}
          currencyCode={currencyAccessor(original)}
        />
      );
    },
  };
};
